import { useCallback, useEffect, useRef, useState } from 'react';
import { cldThumb } from '../../utils/img';
import { apiError, getLatestScrapeRun, startScrapeRun, stopScrapeRun } from '../../api/catalogue';

// Pieces shared by the Catalogue section's pages (Pull & scrape, Full
// catalogue, and the steps still to come). Backend: routers/catalogue.py.

export const MAX_TRIES = 3;

// One entry per row status the backend writes (services/catalogue/scrape.py).
export const STATUS = {
  pending:     { label: 'Waiting',         pill: 'bg-sky-50 text-sky-700 ring-sky-200',       bar: 'bg-sky-300' },
  done:        { label: 'Scraped',         pill: 'bg-green-50 text-green-700 ring-green-200', bar: 'bg-zoddle-green' },
  failed:      { label: 'Failed',          pill: 'bg-pink-50 text-pink-700 ring-pink-200',    bar: 'bg-zoddle-pink' },
  dead_link:   { label: 'Link gone',       pill: 'bg-gray-100 text-gray-600 ring-gray-200',   bar: 'bg-gray-400' },
  unsupported: { label: "Can't read site", pill: 'bg-amber-50 text-amber-700 ring-amber-200', bar: 'bg-amber-400' },
  no_link:     { label: 'No link',         pill: 'bg-white text-gray-500 ring-gray-200',      bar: 'bg-gray-200' },
  not_online:  { label: 'Not online',      pill: 'bg-violet-50 text-violet-700 ring-violet-200', bar: 'bg-violet-300' },
};
export const FILTERS = ['all', 'pending', 'done', 'failed', 'dead_link', 'unsupported', 'no_link', 'not_online'];
export const RESULTS = ['done', 'failed', 'dead_link', 'unsupported'];
// The photo copy job shares the run table (and the one-job-at-a-time rule) with scrape runs.
export const isCopyJob = (run) => run?.scope === 'copy_photos';

export const RUN_END = { done: 'finished', stopped: 'stopped', failed: 'failed', abandoned: 'was cut off by a restart' };

export const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green/40';
export const num = (x) => (x ?? 0).toLocaleString('en-IN');
export const rupees = (x) => (x == null ? null : `₹${Math.round(x).toLocaleString('en-IN')}`);

export function ago(iso) {
  if (!iso) return '—';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export function elapsed(iso) {
  if (!iso) return '';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? 'under a minute' : `${m} min`;
}

export function host(url) {
  try { return new URL(url).host.replace(/^www\./, ''); } catch { return url; }
}

// A small copy of a photo: Cloudinary and Shopify both resize on the fly,
// so a grid of brand photos doesn't download every original.
export function thumbOf(url, w) {
  if (!url) return url;
  if (/res\.cloudinary\.com|cloudfront\.net/i.test(url)) return cldThumb(url, w);
  if (/cdn\.shopify\.com|\/cdn\/shop\//i.test(url)) return `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
  return url;
}

export function looksLikeImage(url) {
  return /res\.cloudinary\.com|cloudfront\.net|cdn\.shopify\.com|\/cdn\/shop\//i.test(url || '')
    || /\.(jpe?g|png|webp|avif|gif)(\?|$)/i.test(url || '');
}

export function slotLabel(source) {
  if (source === 'manual') return 'added by hand';
  const m = /image_url_(\d)/.exec(source || '');
  return m ? `from image slot ${m[1]}` : '';
}

export function PrimaryButton({ children, className = '', ...props }) {
  return (
    <button {...props}
      className={`inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg bg-zoddle-green text-white text-[13px] font-semibold shadow-sm hover:brightness-95 active:brightness-90 disabled:opacity-40 disabled:cursor-not-allowed transition ${focusRing} ${className}`}>
      {children}
    </button>
  );
}

export function SecondaryButton({ children, tone = 'gray', className = '', ...props }) {
  const tones = {
    gray: 'border-gray-200 text-gray-700 hover:bg-gray-50',
    pink: 'border-pink-200 text-zoddle-pink hover:bg-pink-50',
  };
  return (
    <button {...props}
      className={`inline-flex items-center justify-center gap-2 h-9 px-3.5 rounded-lg border bg-white text-[13px] font-medium disabled:opacity-40 disabled:cursor-not-allowed transition ${tones[tone]} ${focusRing} ${className}`}>
      {children}
    </button>
  );
}

export function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.pending;
  return (
    <span className={`inline-flex items-center h-6 px-2 rounded-md text-[12px] font-medium ring-1 ring-inset whitespace-nowrap ${s.pill}`}>
      {s.label}
    </span>
  );
}

export function SegmentBar({ total, counts, className = '' }) {
  return (
    <div className={`flex w-full h-2 rounded-full bg-gray-100 overflow-hidden ${className}`}>
      {total > 0 && RESULTS.map((k) => (counts?.[k] || 0) > 0 && (
        <div key={k} title={`${STATUS[k].label}: ${num(counts[k])}`}
          className={`${STATUS[k].bar} transition-[width] duration-500 ease-out`}
          style={{ width: `${((counts[k] || 0) / total) * 100}%` }} />
      ))}
    </div>
  );
}

// Counts per result. With onPick, each count opens the Full catalogue on it.
export function Legend({ counts, onPick }) {
  return (
    <ul className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-gray-500">
      {RESULTS.map((k) => {
        const body = (
          <>
            <span className={`h-2 w-2 rounded-full ${STATUS[k].bar}`} aria-hidden />
            {STATUS[k].label} <span className="tabular-nums text-gray-800 font-medium">{num(counts?.[k])}</span>
          </>
        );
        return (
          <li key={k}>
            {onPick
              ? <button onClick={() => onPick(k)} className={`inline-flex items-center gap-1.5 rounded hover:text-gray-800 hover:underline underline-offset-2 ${focusRing}`}>{body}</button>
              : <span className="inline-flex items-center gap-1.5">{body}</span>}
          </li>
        );
      })}
    </ul>
  );
}

export function Thumb({ url, size = 'h-10 w-10' }) {
  if (!url) return <div className={`${size} rounded-md bg-gray-100 ring-1 ring-gray-200 shrink-0`} aria-hidden />;
  return <img src={thumbOf(url, 96)} alt="" loading="lazy" referrerPolicy="no-referrer"
    className={`${size} rounded-md object-cover bg-gray-100 ring-1 ring-gray-200 shrink-0`} />;
}

export function PageHeader({ title, children }) {
  return (
    <header>
      <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">{title}</h1>
      {children && <p className="mt-1 text-[13px] text-gray-500 max-w-2xl">{children}</p>}
    </header>
  );
}

export function Notice({ tone = 'pink', icon: Icon, children, className = '' }) {
  const tones = {
    pink: 'bg-pink-50 border-pink-100 text-pink-800',
    green: 'bg-green-50 border-green-100 text-green-800',
    gray: 'bg-gray-50 border-gray-100 text-gray-600',
  };
  return (
    <div className={`flex gap-2 rounded-lg border px-3 py-2.5 text-[12.5px] ${tones[tone]} ${className}`}>
      {Icon && <Icon size={15} className="shrink-0 mt-0.5" aria-hidden />}
      <div>{children}</div>
    </div>
  );
}

export const CATALOGUE_CSS = `
@keyframes catalogue-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes catalogue-slide { from { transform: translateX(24px); opacity: 0; } to { transform: none; opacity: 1; } }
@keyframes catalogue-stream { from { background-position: 0 0; } to { background-position: 22.63px 0; } }
@keyframes catalogue-dash { to { stroke-dashoffset: -16; } }
.catalogue-stream {
  background-image: repeating-linear-gradient(-45deg, rgb(125 211 252) 0 8px, rgb(186 230 253) 8px 16px);
}
@media (prefers-reduced-motion: no-preference) {
  .catalogue-fade { animation: catalogue-fade 0.15s ease-out; }
  .catalogue-drawer { animation: catalogue-slide 0.18s ease-out; }
  .catalogue-stream { animation: catalogue-stream 0.8s linear infinite; }
  .catalogue-dash { animation: catalogue-dash 0.6s linear infinite; }
}
`;

// The latest scrape run, polled every 2 s while it runs. onChange('progress')
// fires when more designs have been read, onChange('finished') when it ends,
// so a page can reload what it shows.
export function useScrapeRun(onChange) {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const wasRunning = useRef(false);
  const lastProcessed = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const running = run?.status === 'running';

  const refresh = useCallback(async () => {
    try {
      const r = await getLatestScrapeRun();
      const isRunning = r?.status === 'running';
      setRun(r);
      if (wasRunning.current && !isRunning) onChangeRef.current?.('finished');
      else if (isRunning && r.processed !== lastProcessed.current) onChangeRef.current?.('progress');
      wasRunning.current = isRunning;
      lastProcessed.current = r?.processed ?? null;
    } catch {
      /* a 401 is handled app-wide; anything else is retried on the next poll */
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(refresh, 2000);
    return () => clearInterval(t);
  }, [running, refresh]);

  const start = useCallback(async (scope, designIds, extra) => {
    setBusy(true); setError('');
    try {
      const r = await startScrapeRun(scope, designIds, extra);
      setRun(r);
      wasRunning.current = true;
      lastProcessed.current = 0;
    } catch (e) {
      setError(apiError(e, 'Could not start the run'));
      refresh();
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  const stop = useCallback(async () => {
    if (!run) return;
    setBusy(true);
    try { setRun(await stopScrapeRun(run.run_id)); }
    catch (e) { setError(apiError(e, 'Could not stop the run')); refresh(); }
    finally { setBusy(false); }
  }, [run, refresh]);

  return { run, running, busy, error, start, stop, refresh };
}
